import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import './Women.css';
import appointmentService from '../../services/appointment.service';

type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

interface Service {
  id: number;
  name: string;
  price: string; // Display price text
  priceValue: number;
  duration: number; // in minutes. 0 implies consultation needed
  requiresConsultation?: boolean;
}

const FORCE_CONSULTATION = true;

const isWomenPackage = (pkg: any) => {
  const durationMinutes = pkg.durationMinutes ?? pkg.duration_minutes ?? pkg.duration ?? 0;
  const requiresCall = pkg.requiresCall ?? pkg.requires_call ?? false;
  return Boolean(requiresCall) || durationMinutes == 0;
};

const Women: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [servicesLoading, setServicesLoading] = useState<boolean>(true);
  const [servicesError, setServicesError] = useState<string | null>(null);
  const [date, setDate] = useState<Value>(new Date());
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [userDetails, setUserDetails] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    email: ''
  });

  // Opening Hours Config
  // 0 = Sunday (Closed), 1 = Monday, etc.
  const openingHours: { [key: number]: { open: string, close: string } | null } = {
    0: null, // Sunday: Closed
    1: null, // Monday
    2: { open: '09:00', close: '20:00' }, // Tuesday
    3: { open: '09:00', close: '17:00' }, // Wednesday
    4: { open: '09:00', close: '20:00' }, // Thursday
    5: { open: '09:00', close: '20:00' }, // Friday
    6: { open: '09:00', close: '17:00' }, // Saturday
  };

  const isDayClosed = (date: Date) => {
    const day = date.getDay();
    return openingHours[day] === null;
  };

  const generateTimeSlots = (selectedDate: Date, duration: number) => {
    const dayConfig = openingHours[selectedDate.getDay()];
    if (!dayConfig || duration === 0) return [];

    const slots: string[] = [];

    // Parse open and close times
    const [openHour, openMinute] = dayConfig.open.split(':').map(Number);
    const [closeHour, closeMinute] = dayConfig.close.split(':').map(Number);

    let current = new Date(selectedDate);
    current.setHours(openHour, openMinute, 0, 0);

    const closeTime = new Date(selectedDate);
    closeTime.setHours(closeHour, closeMinute, 0, 0);

    // Generate slots
    while (true) {
      // Calculate end time of this slot
      const potentialEnd = new Date(current.getTime() + duration * 60000);

      if (potentialEnd > closeTime) break;

      // Add to list formatted HH:mm
      const timeString = current.toLocaleTimeString('el-GR', { hour: '2-digit', minute: '2-digit', hour12: false });
      slots.push(timeString);

      // Step: 15 minutes for flexibility
      current.setMinutes(current.getMinutes() + 15);
    }

    return slots;
  };

  // Fetch services from backend (packages table)
  useEffect(() => {
    setServicesLoading(true);
    setServicesError(null);
    appointmentService.getPackages()
      .then((data: any[]) => {
        const mapped = data
          .map(pkg => {
            const durationMinutes = pkg.durationMinutes ?? pkg.duration_minutes ?? pkg.duration ?? 0;
            const requiresCall = pkg.requiresCall ?? pkg.requires_call ?? false;
            const name = pkg.name ?? pkg.serviceName ?? '';
            if (!name || !isWomenPackage(pkg)) return null;
            const priceValue = Number(pkg.price ?? 0);
            const priceText = Number.isFinite(priceValue) ? `${priceValue}€` : '';
            const requiresConsultation = FORCE_CONSULTATION || requiresCall || durationMinutes == 0;
            return {
              id: pkg.id,
              name,
              price: priceText,
              priceValue,
              duration: durationMinutes,
              requiresConsultation
            } as Service;
          })
          .filter(Boolean) as Service[];
        setServices(mapped);
      })
      .catch(() => {
        setServices([]);
        setServicesError('Failed to load services.');
      })
      .finally(() => setServicesLoading(false));
  }, []);

  // Fetch booked slots for the selected date
  useEffect(() => {
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      appointmentService.getAppointmentsForDate(dateStr)
        .then((appointments: any[]) => {
          const booked: string[] = appointments
            .filter(app => app.status !== 'CANCELLED')
            .map(app => app.time?.slice(0, 5))
            .filter(Boolean);
          setBookedSlots(booked);
        })
        .catch(() => setBookedSlots([]));
    } else {
      setBookedSlots([]);
    }
  }, [date]);

  useEffect(() => {
    if (date instanceof Date && selectedService && !selectedService.requiresConsultation) {
      let slots = generateTimeSlots(date, selectedService.duration);
      if (bookedSlots.length > 0) {
        slots = slots.filter(slot => !bookedSlots.includes(slot));
      }
      setAvailableSlots(slots);
      if (selectedTime && (bookedSlots.includes(selectedTime) || !slots.includes(selectedTime))) {
        setSelectedTime(null);
      }
    } else {
      setAvailableSlots([]);
      if (selectedTime) {
        setSelectedTime(null);
      }
    }
  }, [date, selectedService, bookedSlots, selectedTime]);

  const handleDateChange = (nextValue: Value) => {
    setDate(nextValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleBookAppointment = () => {
    if (!date || !selectedTime || !selectedService) return;

    if (bookedSlots.includes(selectedTime)) {
      alert("Selected time is no longer available. Please choose another time.");
      setSelectedTime(null);
      return;
    }

    if (!userDetails.firstName || !userDetails.lastName || !userDetails.phone || !userDetails.email) {
      alert("Παρακαλώ συμπληρώστε όλα τα πεδία.");
      return;
    }

    const selectedDate = date instanceof Date ? date : new Date();
    // Format date as YYYY-MM-DD
    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDate.getDate()).padStart(2, '0');
    const dateStr = `${year}-${month}-${day}`;

    appointmentService.createAppointment(
      selectedService.name,
      dateStr,
      selectedTime,
      selectedService.priceValue,
      selectedService.duration,
      userDetails.firstName,
      userDetails.lastName,
      userDetails.email,
      userDetails.phone,
      undefined
    ).then(() => {
      alert("Το ραντεβού κλείστηκε επιτυχώς!");
      setSelectedService(null);
      setSelectedTime(null);
      setDate(new Date());
      setUserDetails({ firstName: '', lastName: '', phone: '', email: '' });
    }).catch(err => {
      console.error("Booking error:", err);
      alert("Παρουσιάστηκε σφάλμα. Παρακαλώ δοκιμάστε ξανά.");
    });
  };

  return (
    <div className="women-booking-container">
      <div className="booking-wrapper">
        <h2 className="booking-title">Γυναικειο Κομμωτηριο - Κομμωσεις</h2>
        <p className="intro-text">Κλείστε ραντεβού για τις υπηρεσίες μας.</p>

        <div className="booking-content">

          {/* Step 1: Service Selection */}
          <div className="section service-section">
            <h3>1. Επιλέξτε Υπηρεσία</h3>
            <div className="services-list">
              {servicesLoading && <p className="no-slots">Φόρτωση υπηρεσιών...</p>}
              {!servicesLoading && servicesError && <p className="no-slots">{servicesError}</p>}
              {!servicesLoading && !servicesError && services.length === 0 && (
                <p className="no-slots">Δεν υπάρχουν διαθέσιμες υπηρεσίες.</p>
              )}
              {services.map(service => (
                <div
                  key={service.id}
                  className={`service-card ${selectedService?.id === service.id ? 'active' : ''}`}
                  onClick={() => setSelectedService(service)}
                >
                  <div className="service-info">
                    <span className="service-name">{service.name}</span>
                    <span className="service-duration">
                      {service.requiresConsultation ? "Απαιτείται Συμβουλευτική" : `${service.duration} λεπτά`}
                    </span>
                  </div>
                  <span className="service-price">{service.price}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Step 2: Date Selection (Hidden for Consultations) */}
          <div className={`section calendar-section ${!selectedService || selectedService.requiresConsultation ? 'disabled' : ''}`}>
            <h3>2. Επιλέξτε Ημερομηνία</h3>
            <Calendar
              onChange={handleDateChange}
              value={date}
              className="react-calendar-custom"
              tileDisabled={({ date }) => isDayClosed(date)}
              minDate={new Date()}
            />
          </div>

          {/* Step 3: Time Selection or Message */}
          <div className="section time-section">
            <h3>3. Επιλέξτε Ώρα</h3>

            {selectedService?.requiresConsultation ? (
              <div className="consultation-message">
                <p>Για την υπηρεσία <strong>{selectedService.name}</strong> απαιτείται συμβουλευτική πριν το κλείσιμο ραντεβού.</p>
                <p className="phone-number">Τηλ: 210 6394347</p>
              </div>
            ) : (
              <>
                <p className="selected-date-label">
                  {date instanceof Date ? date.toLocaleDateString('el-GR', { weekday: 'long', day: 'numeric', month: 'long' }) : ''}
                  {date instanceof Date && openingHours[date.getDay()] ?
                    ` (${openingHours[date.getDay()]?.open} - ${openingHours[date.getDay()]?.close})` :
                    ' (Closed)'
                  }
                </p>

                {!selectedService ? (
                  <p className="no-slots">Παρακαλώ επιλέξτε μια υπηρεσία πρώτα.</p>
                ) : availableSlots.length > 0 ? (
                  <div className="time-slots-grid">
                    {availableSlots.map((time) => (
                      <button
                        key={time}
                        className={`time-slot ${selectedTime === time ? 'selected' : ''}`}
                        onClick={() => setSelectedTime(time)}
                      >
                        {time}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="no-slots">Δεν υπάρχουν διαθέσιμες ώρες για την επιλεγμένη ημερομηνία.</p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Step 4: User Details */}
        {selectedService && selectedTime && !selectedService.requiresConsultation && (
          <div className="section details-section">
            <h3>4. Συμπληρώστε τα Στοιχεία σας</h3>
            <div className="details-form">
              <div className="form-group">
                <label>Όνομα</label>
                <input
                  type="text"
                  name="firstName"
                  value={userDetails.firstName}
                  onChange={handleInputChange}
                  placeholder="Όνομα"
                  required
                />
              </div>
              <div className="form-group">
                <label>Επώνυμο</label>
                <input
                  type="text"
                  name="lastName"
                  value={userDetails.lastName}
                  onChange={handleInputChange}
                  placeholder="Επώνυμο"
                  required
                />
              </div>
              <div className="form-group">
                <label>Τηλέφωνο</label>
                <input
                  type="tel"
                  name="phone"
                  value={userDetails.phone}
                  onChange={handleInputChange}
                  placeholder="Τηλέφωνο"
                  required
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  name="email"
                  value={userDetails.email}
                  onChange={handleInputChange}
                  placeholder="Email"
                  required
                />
              </div>
            </div>
          </div>
        )}

        {selectedService && selectedTime && !selectedService.requiresConsultation && (
          <div className="booking-actions">
            <div className="summary">
              <p>Υπηρεσία: <strong>{selectedService.name}</strong></p>
              <p>Ημερομηνία: <strong>{date instanceof Date ? date.toLocaleDateString() : ''}</strong> Ώρα: <strong>{selectedTime}</strong></p>
              <p>Τιμή: <strong>{selectedService.price}</strong></p>
            </div>
            <button className="book-btn" onClick={handleBookAppointment}>
              Κλείστε Ραντεβού
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Women;
